// src/pages/Employee/CreateEmployee/validators.ts

import { EmployeeFormData, StepErrors } from "./employee.types";

const isEmail = (v: string) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Store API validation state outside the validator
// This will be set by the Step1 component
let apiValidationState = {
  isChecking: false,
  isAvailable: true,
  error: null as string | null,
};

// Function to update API validation state (called from Step1)
export const updateEmployeeCodeValidation = (
  isChecking: boolean,
  isAvailable: boolean,
  error: string | null,
) => {
  apiValidationState = {
    isChecking,
    isAvailable,
    error,
  };
};

// Function to reset API validation state
export const resetEmployeeCodeValidation = () => {
  apiValidationState = {
    isChecking: false,
    isAvailable: true,
    error: null,
  };
};

export const validateStep = (
  stepIndex: number,
  form: EmployeeFormData,
): StepErrors => {
  const errors: StepErrors = {};

  if (stepIndex === 0) {
    const d = form.basicInfo;

    // Name validation
    if (!d.name.trim()) errors.name = "Name is required";

    // ✅ Employee Code validation - Updated for alphanumeric
    if (!d.employee_code.trim()) {
      errors.employee_code = "Employee code is required";
    } else if (d.employee_code.length < 2) {
      errors.employee_code = "Employee code must be at least 2 characters";
    } else if (!/^[A-Z0-9]+$/.test(d.employee_code)) {
      errors.employee_code = "Employee code must contain only letters and numbers";
    }
    // Note: API validation (availability) is handled by Step1 component

    // Phone validation
    if (!d.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!/^\+?\d{10,13}$/.test(d.phone)) {
      errors.phone = "Enter a valid phone number";
    }

    // Aadhar validation
    if (d.aadharr && d.aadharr.length !== 12) {
      errors.aadharr = "Aadhar number must be 12 digits";
    }

    // Email validation
    if (!isEmail(d.personal_email)) {
      errors.personal_email = "Enter a valid email";
    }
    if (!isEmail(d.official_email)) {
      errors.official_email = "Enter a valid email";
    }
  }

  if (stepIndex === 1) {
    const d = form.personalFamily;
    if (d.age && (Number(d.age) < 18 || Number(d.age) > 80)) {
      errors.age = "Enter a realistic age";
    }
  }

  if (stepIndex === 2) {
    const d = form.address;
    if (d.present_pincode && d.present_pincode.length !== 6) {
      errors.present_pincode = "Pincode must be 6 digits";
    }
  }

  if (stepIndex === 3) {
    const d = form.jobSalaryBank;

    // Required fields
    if (!d.date_of_joining) errors.date_of_joining = "Date of joining is required";
    if (!d.employee_type) errors.employee_type = "Employee type is required";
    if (!d.nature_of_job) errors.nature_of_job = "Nature of job is required";

    // ✅ Employee locations validation - Only required when geofence is enabled
    if (d.geofence === true) {
      if (!d.employee_location_ids || d.employee_location_ids.length === 0) {
        errors.employee_location_ids = "Please select at least one location when Geo-Fence is enabled";
      }
    }
    // If geofence is false, location selection is optional (no validation)

    // Salary validation
    if (d.basic_salary && Number(d.basic_salary) < 0) {
      errors.basic_salary = "Salary cannot be negative";
    }

    // Bank validation
    if (d.bank_account_no && d.bank_account_no.length < 6) {
      errors.bank_account_no = "Enter a valid account number";
    }
    if (d.ifsc_code && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(d.ifsc_code)) {
      errors.ifsc_code = "Enter a valid IFSC code";
    }
  }

  // Step 4 (qualifications) has no required fields — freely add/skip.

  if (stepIndex === 5) {
    const d = form.documents;
    if (d.pan_number && !/^[A-Z]{5}\d{4}[A-Z]$/.test(d.pan_number)) {
      errors.pan_number = "Enter a valid PAN (e.g. ABCDE1234F)";
    }
  }

  if (stepIndex === 6) {
    const d = form.emergencyReference;

    // Emergency Contact - Optional validations (only validate if field has value)
    if (d.emergency_mobile && d.emergency_mobile.trim()) {
      if (!/^\+?\d{10,13}$/.test(d.emergency_mobile.trim())) {
        errors.emergency_mobile = "Enter a valid mobile number";
      }
    }

    if (d.emergency_email && d.emergency_email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.emergency_email.trim())) {
        errors.emergency_email = "Enter a valid email address";
      }
    }

    // Reference - Optional validations (only validate if field has value)
    if (d.reference_mobile && d.reference_mobile.trim()) {
      if (!/^\+?\d{10,13}$/.test(d.reference_mobile.trim())) {
        errors.reference_mobile = "Enter a valid mobile number";
      }
    }

    if (d.reference_email && d.reference_email.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.reference_email.trim())) {
        errors.reference_email = "Enter a valid email address";
      }
    }
  }

  return errors;
};